import { useState } from 'react';
import { Modal, TextInput, Button, Stack, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { partnersApi } from '../../services/api/partners';
import type { Partner, CreatePartnerRequest } from '../../types';
import { formValidators } from '../../utils/validation';

interface AddPartnerModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: (partner: Partner) => void;
}

export function AddPartnerModal({ opened, onClose, onSuccess }: AddPartnerModalProps) {
  const [loading, setLoading] = useState(false);

  const form = useForm<CreatePartnerRequest>({
    initialValues: { name: '', code: '' },
    validate: {
      name: formValidators.required('Partner name'),
      code: formValidators.partnerCode,
    },
  });

  const handleSubmit = async (values: CreatePartnerRequest) => {
    try {
      setLoading(true);
      const partner = await partnersApi.create({ ...values, code: values.code.toUpperCase() });
      notifications.show({ title: 'Success', message: 'Partner created successfully', color: 'green' });
      form.reset();
      onSuccess(partner);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create partner',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Create Partner" size="md">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Partner Name"
            placeholder="e.g., Acme Corporation"
            required
            {...form.getInputProps('name')}
          />

          <TextInput
            label="Partner Code"
            placeholder="e.g., AC"
            description="2-4 uppercase letters. This cannot be changed after creation."
            required
            maxLength={4}
            {...form.getInputProps('code')}
            onChange={e => {
              const value = e.currentTarget.value.toUpperCase().replace(/[^A-Z]/g, '');
              form.setFieldValue('code', value);
            }}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Create Partner
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
